/**
 * CONDITION SELECTOR COMPONENT
 * 
 * A reusable dropdown for selecting item condition.
 * Automatically shows appropriate options based on item type.
 * Includes tooltip descriptions for each condition.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { getConditionsForType } from '../../constants/destashConditions';

interface ConditionSelectorProps {
  itemType: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  showDescription?: boolean;
  className?: string;
}

const ConditionSelector: React.FC<ConditionSelectorProps> = ({
  itemType,
  value,
  onChange,
  required = false,
  disabled = false,
  showDescription = true,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const conditions = getConditionsForType(itemType);
  const selectedCondition = conditions.find(c => c.value === value);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex(prev => (prev < conditions.length - 1 ? prev + 1 : 0));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setFocusedIndex(prev => (prev > 0 ? prev - 1 : conditions.length - 1));
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(conditions.findIndex(c => c.value === value) || 0);
        } else if (focusedIndex >= 0) {
          onChange(conditions[focusedIndex].value);
          setIsOpen(false);
          buttonRef.current?.focus();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      case 'Home':
        e.preventDefault();
        if (isOpen) setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        if (isOpen) setFocusedIndex(conditions.length - 1);
        break;
    }
  }, [disabled, isOpen, conditions, focusedIndex, value, onChange]);

  // Scroll focused option into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listRef.current) {
      const focusedElement = listRef.current.children[focusedIndex] as HTMLElement;
      focusedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, focusedIndex]);

  return (
    <div className={`relative ${className}`}>
      {/* Label */}
      <label id="condition-label" className="block text-sm font-bold text-light-secondary mb-1 font-body">
        Condition {required && <span className="text-red-500">*</span>}
      </label>

      {/* Custom Dropdown Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby="condition-label"
        className={`w-full flex items-center justify-between px-4 py-2.5 bg-white border rounded-lg text-left transition-default ${
          disabled
            ? 'border-gray-300 bg-gray-100 cursor-not-allowed text-light-muted'
            : isOpen
              ? 'border-gray-400 ring-2 ring-gray-300'
              : 'border-gray-300 hover:border-gray-400'
        } ${!value && required ? 'border-amber-300' : ''}`}
      >
        <span className={value ? 'text-light-primary' : 'text-light-muted'}>
          {selectedCondition?.label || 'Select condition...'}
        </span>
        <ChevronDown className={`w-5 h-5 text-light-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Options List */}
          <div
            ref={listRef}
            role="listbox"
            aria-labelledby="condition-label"
            aria-activedescendant={focusedIndex >= 0 ? `condition-option-${focusedIndex}` : undefined}
            onKeyDown={handleKeyDown}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-64 overflow-y-auto"
          >
            {conditions.map((condition, index) => (
              <button
                key={condition.value}
                id={`condition-option-${index}`}
                type="button"
                role="option"
                aria-selected={value === condition.value}
                onClick={() => {
                  onChange(condition.value);
                  setIsOpen(false);
                  buttonRef.current?.focus();
                }}
                onMouseEnter={() => setFocusedIndex(index)}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-default ${
                  value === condition.value
                    ? 'bg-gray-100 text-light-primary'
                    : focusedIndex === index
                      ? 'bg-gray-50 text-light-primary'
                      : 'hover:bg-gray-100 text-light-secondary'
                } ${focusedIndex === index ? 'ring-2 ring-inset ring-purple-500' : ''}`}
              >
                <div className="flex-1">
                  <span className={`font-medium ${value === condition.value ? 'text-light-primary' : ''}`}>
                    {condition.label}
                  </span>
                  {showDescription && condition.description && (
                    <p className="text-xs text-light-muted mt-0.5">
                      {condition.description}
                    </p>
                  )}
                </div>
                {value === condition.value && (
                  <span className="text-green-600 ml-2">✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Selected Description (below dropdown) */}
      {showDescription && selectedCondition?.description && !isOpen && (
        <p className="mt-1 text-xs text-light-muted flex items-center gap-1 font-body">
          <HelpCircle className="icon-xs" />
          {selectedCondition.description}
        </p>
      )}

      {/* Required Warning */}
      {required && !value && (
        <p className="mt-1 text-xs text-amber-600 font-body">
          Please select a condition to list on marketplace
        </p>
      )}
    </div>
  );
};

export default ConditionSelector;
